import { useContext, useMemo, useState } from 'react'
import { useNavigate as useReactRouterNavigate } from 'react-router-dom'
import { Form, Input, Modal } from 'antd'
import { DownloadOutlined, HomeOutlined, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import { AuthContext } from '@/contexts/AuthContext'
import { useAppNavigate, useMunicipalityFromHost } from '@/hooks'
import { authApi, getErrorMessage, isHandledAuthError } from '@/services'
import type { NavOpts } from '@/routes'
import { getEnvTheme } from '@/config/envTheme'
import { HEADER_DOWNLOAD_LINKS, type DownloadLink } from '@/constants'

interface SystemHeaderProps {
  breadcrumbs?: Array<{ label: string; page?: string; navigateOpts?: NavOpts }>
  currentUser?: string
  viewerRole?: string
}

const ROLE_LABELS: Record<string, string> = {
  SYSTEM_ADMIN: 'システム管理者',
  ADMIN: '管理者',
  BUSINESS_CENTER: '業務センター',
  RECEPTION_COUNTER: '受付窓口',
  CALL_CENTER_SV: 'コールセンター（SV）',
  CALL_CENTER_AGENT: 'コールセンター（一般）',
  MUNICIPALITY_STAFF: '自治体職員',
}

const SystemHeader = ({ breadcrumbs = [], currentUser = '山田 太郎', viewerRole }: SystemHeaderProps) => {
  const auth = useContext(AuthContext)
  const navigate = useAppNavigate()
  const routerNavigate = useReactRouterNavigate()
  const fromHost = useMunicipalityFromHost()
  const [pwOpen, setPwOpen] = useState(false)
  const [pwForm] = Form.useForm()
  const [pwLoading, setPwLoading] = useState(false)
  const envTheme = getEnvTheme()

  const displayUser = auth?.session?.displayName ?? currentUser
  // 自治体名: 自治体所属ユーザは session を優先（即時表示）、SYSTEM_ADMIN 等で session が空なら
  // URL ホスト名から推定（meguro.* → 目黒区）。共通ホスト（stg.lifekyufu.jp 等）では非表示。
  const displayMunicipality = (auth?.session?.municipality?.trim() || fromHost.name) || null
  // 表示優先順位: 明示渡しの viewerRole → session.roleDisplay（後端 i18n 済）→
  // session.role enum key（フォールバック、ローカル ROLE_LABELS で復元）→ '管理者'。
  const displayRole = useMemo(() => {
    const rawRole = viewerRole || auth?.session?.roleDisplay || auth?.session?.role
    if (!rawRole) return '管理者'
    if (/^[A-Z_]+$/.test(rawRole)) return ROLE_LABELS[rawRole] ?? rawRole
    return rawRole
  }, [auth?.session?.role, auth?.session?.roleDisplay, viewerRole])

  // 管理No.16: 資料（よくあるご質問 / マニュアル）のダウンロード。
  // public/downloads/ の静的アセットを取得して保存名を日本語化して保存する。
  // 実ファイル未配置時は SPA フォールバックで index.html(200) が返るため、HTML を弾いて「準備中」とする。
  const handleDownload = async (item: DownloadLink) => {
    // ファイル名は日本語・全角【】を含むため percent-encoding してから取得する（S3 キーは UTF-8 で一致）。
    const href = `/downloads/${encodeURIComponent(item.fileName)}`
    try {
      const res = await fetch(href, { cache: 'no-store' })
      const contentType = res.headers.get('content-type') ?? ''
      if (!res.ok || contentType.includes('text/html')) throw new Error('not-available')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = item.fileName
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      Modal.info({ title: item.label, content: '資料は準備中です。公開までしばらくお待ちください。' })
    }
  }

  const submitPassword = async () => {
    try {
      const values = await pwForm.validateFields()

      setPwLoading(true)
      await authApi.changePassword(values.current, values.next)
      setPwOpen(false)
      pwForm.resetFields()
      Modal.success({ content: 'パスワードを変更しました。' })
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'errorFields' in err) return
      if (isHandledAuthError(err)) return
      // changePassword は AWS Amplify 直呼びで axios interceptor を通らないため、
      // ここで表示しないと無音失敗になる。
      Modal.error({ content: getErrorMessage(err, 'パスワード変更に失敗しました。') })
    } finally {
      setPwLoading(false)
    }
  }

  return (
    <header className={`relative z-50 bg-gradient-to-r ${envTheme.headerGradient} text-white shadow-lg`}>
      <div className="mx-auto w-full max-w-[1600px] px-6 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center space-x-3">
            <button
              type="button"
              onClick={() => navigate('home')}
              className={`flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center rounded-lg bg-white shadow-md transition ${envTheme.logoBtnHover}`}
            >
              <HomeOutlined className={`!text-xl ${envTheme.logoIconColor}`} />
            </button>
            <div className="min-w-0">
              {displayMunicipality && (
                <p className={`text-xs leading-tight ${envTheme.subText} truncate`}>{displayMunicipality}</p>
              )}
              <h1 className="text-xl font-bold tracking-wide flex items-center gap-2">
                <span>生活保護給付管理ツール</span>
                {envTheme.showBadge && (
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-bold tracking-wider ${envTheme.badgeBg} ${envTheme.badgeText}`}
                    title={envTheme.label}
                  >
                    {envTheme.label}
                  </span>
                )}
              </h1>
              {breadcrumbs.length > 0 && (
                <div className={`mt-0.5 flex items-center text-xs ${envTheme.subText}`}>
                  <button type="button" onClick={() => navigate('home')} className="cursor-pointer transition hover:text-white">
                    ホーム
                  </button>
                  {breadcrumbs.map((breadcrumb, index) => (
                    <span key={`${breadcrumb.label}-${index}`} className="flex items-center">
                      <span className="mx-1.5">›</span>
                      {breadcrumb.page ? (
                        <button
                          type="button"
                          onClick={() => navigate(breadcrumb.page!, breadcrumb.navigateOpts)}
                          className="cursor-pointer transition hover:text-white"
                        >
                          {breadcrumb.label}
                        </button>
                      ) : (
                        <span className="font-medium text-white">{breadcrumb.label}</span>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          {HEADER_DOWNLOAD_LINKS.length > 0 && (
            <nav className="hidden shrink-0 items-center gap-4 md:flex" aria-label="資料ダウンロード">
              {HEADER_DOWNLOAD_LINKS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => void handleDownload(item)}
                  className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-white/90 underline-offset-4 transition hover:text-white hover:underline"
                  title={`${item.label}をダウンロード`}
                >
                  <DownloadOutlined className="!text-xs" />
                  {item.label}
                </button>
              ))}
            </nav>
          )}
          <div className="flex shrink-0 items-center gap-2">
            <div className="flex max-w-[10rem] flex-col items-end justify-center pr-1 text-right">
              <p className="max-w-full truncate text-sm font-medium leading-tight text-white">{displayUser}</p>
              <p className={`text-xs leading-tight ${envTheme.subText}`}>{displayRole}</p>
            </div>
            <button
              type="button"
              onClick={() => routerNavigate('/settings/mfa')}
              className="cursor-pointer whitespace-nowrap rounded-lg border border-white/50 bg-white/10 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-white/20 inline-flex items-center gap-1 sm:text-sm"
              title="多要素認証 (MFA) を設定"
            >
              <SafetyCertificateOutlined />
              MFA 設定
            </button>
            <button
              type="button"
              onClick={() => setPwOpen(true)}
              className="cursor-pointer whitespace-nowrap rounded-lg border border-white/50 bg-white/10 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-white/20 sm:text-sm"
            >
              パスワード変更
            </button>
            <button
              type="button"
              onClick={() => auth?.logout?.()}
              className={`cursor-pointer whitespace-nowrap rounded-lg bg-white px-3 py-1.5 text-xs font-medium ${envTheme.logoutText} shadow-md transition ${envTheme.logoutHoverBg} hover:shadow-lg sm:px-4 sm:text-sm`}
            >
              ログアウト
            </button>
          </div>
        </div>
      </div>

      <Modal
        open={pwOpen}
        title="パスワード変更"
        onCancel={() => {
          setPwOpen(false)
          pwForm.resetFields()
        }}
        onOk={submitPassword}
        confirmLoading={pwLoading}
        okText="変更"
        cancelText="キャンセル"
        destroyOnHidden
      >
        <Form form={pwForm} layout="vertical" className="mt-4 customer-validation-form">
          <Form.Item
            name="current"
            label="現在のパスワード"
            rules={[{ required: true, message: '現在のパスワードを入力してください。' }]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="next"
            label="新しいパスワード"
            rules={[
              { required: true, message: 'パスワードを入力してください。' },
              { min: 8, message: 'パスワードは8文字以上入力してください。' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve()
                  if (!/[a-z]/.test(value)) return Promise.reject('英小文字を含めてください。')
                  if (!/[A-Z]/.test(value)) return Promise.reject('英大文字を含めてください。')
                  if (!/\d/.test(value)) return Promise.reject('数字を含めてください。')
                  if (!/[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=]/.test(value)) return Promise.reject('記号を含めてください。')
                  return Promise.resolve()
                },
              },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirm"
            label="新しいパスワード（確認）"
            dependencies={['next']}
            rules={[
              { required: true, message: 'パスワードを入力してください。' },
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  if (!value || getFieldValue('next') === value) return Promise.resolve()
                  return Promise.reject('新しいパスワードと確認が一致しません。')
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </header>
  )
}

export default SystemHeader
